/*
  Black Friday 2026 insider price (docs/bf-insider-price.md).

  The discount itself is a Shopify Function on a code discount ("Black Friday 2026 insider"): the
  cart and checkout price comes from Shopify, never from here. This file only DISPLAYS that price
  on product surfaces while the visitor carries a BF code, so the storefront agrees with the cart.

  - The codes are never in the page. `layout/theme.liquid` renders `window.__bfInsiderConfig.hashes`
    (SHA-256 of each lower-cased code from the theme setting), and a code is recognised by hash.
  - The code arrives through Shopify's own `/discount/<CODE>` link, which sets the `discount_code`
    cookie and applies the code to the cart. This file makes no request to `/cart/*` at all.
  - Once recognised, the code becomes the stored cart code (`cart-discount:code`) and the welcome
    cache is cleared, so the existing cart-discount machinery and the welcome element see it.
    `welcome-discount-price.js` asks `window.bfInsider.isCode()` and stands down for BF codes.
  - A valid `custom.bf_fast_pris` target takes precedence. Otherwise the price is
    `variant.price × (100 − pct) / 100`, rounded down (whole kroner in DKK, x,95
    otherwise), pct from `custom.bf_rabat_pct` or the base rate, exactly as the function computes
    it. compare_at is never used.
  - The struck reference is the lowest price of the last 30 days (variant metafield
    `custom.bf_laveste_30d`, per currency, otherwise the current price). The markup decides where
    it shows (snippets/bf-strike-without-30d.liquid): DE strikes it on the PDP only, the other
    markets also on cards. No market shows a 30-day line, because the struck price itself is the
    Omnibus art. 6a / PAngV § 11 reference; Peter decided this 02/11/2026.
*/
(() => {
  const CART_DISCOUNT_CODE_KEY = 'cart-discount:code';
  const WELCOME_CACHE_KEYS = ['welcome-discount:percent', 'welcome-discount:code'];
  const CODE_CHANGED_EVENT = 'welcome-discount:code-changed';
  const ACTIVE_CLASS = 'bf-insider-active';
  const config = window.__bfInsiderConfig || {};
  const hashes = new Set((config.hashes || []).map((h) => String(h).toLowerCase()));

  /* Synchronous SHA-256 (FIPS 180-4) so the welcome element can ask isCode() without awaiting. */
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);

  function sha256(text) {
    const bytes = new TextEncoder().encode(text);
    const bitLength = bytes.length * 8;
    const padded = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000));
    view.setUint32(padded.length - 4, bitLength >>> 0);

    const H = new Uint32Array([
      0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const W = new Uint32Array(64);
    const rotr = (x, n) => (x >>> n) | (x << (32 - n));

    for (let offset = 0; offset < padded.length; offset += 64) {
      for (let i = 0; i < 16; i += 1) W[i] = view.getUint32(offset + i * 4);
      for (let i = 16; i < 64; i += 1) {
        const s0 = rotr(W[i - 15], 7) ^ rotr(W[i - 15], 18) ^ (W[i - 15] >>> 3);
        const s1 = rotr(W[i - 2], 17) ^ rotr(W[i - 2], 19) ^ (W[i - 2] >>> 10);
        W[i] = (W[i - 16] + s0 + W[i - 7] + s1) >>> 0;
      }
      let [a, b, c, d, e, f, g, h] = H;
      for (let i = 0; i < 64; i += 1) {
        const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
        const t1 = (h + S1 + ((e & f) ^ (~e & g)) + K[i] + W[i]) >>> 0;
        const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
        const t2 = (S0 + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
        h = g;
        g = f;
        f = e;
        e = (d + t1) >>> 0;
        d = c;
        c = b;
        b = a;
        a = (t1 + t2) >>> 0;
      }
      H[0] += a; H[1] += b; H[2] += c; H[3] += d; H[4] += e; H[5] += f; H[6] += g; H[7] += h;
    }
    return Array.from(H, (word) => word.toString(16).padStart(8, '0')).join('');
  }

  function normalize(code) {
    return String(code || '').trim().toLowerCase();
  }

  const hashCache = new Map();
  function isCode(code) {
    if (config.expiresAt && Date.now() > Date.parse(config.expiresAt)) return false;
    const normalized = normalize(code);
    if (!normalized || !hashes.size) return false;
    if (!hashCache.has(normalized)) hashCache.set(normalized, hashes.has(sha256(normalized)));
    return hashCache.get(normalized);
  }

  function readLocalCode() {
    try {
      return normalize(localStorage.getItem(CART_DISCOUNT_CODE_KEY));
    } catch {
      return '';
    }
  }

  function readCookieCode() {
    const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/i);
    if (!match?.[1]) return '';
    try {
      return normalize(decodeURIComponent(match[1]));
    } catch {
      return normalize(match[1]);
    }
  }

  // Only unpredictable personal codes fetch a single public hash bucket. Raw codes and the
  // generator key never reach the theme. Missing/malformed/failed buckets grant no access.
  const pendingBuckets = new Map();
  function loadPersonalCode(code, refresh = false) {
    const match = normalize(code).match(/^w([0-3][0-9a-f])[0-9a-f]{2}[a-z2-7]{10}$/);
    if (!match || !config.personalHashUrl) return Promise.resolve();
    const bucket = match[1];
    if (refresh) pendingBuckets.delete(bucket);
    if (!pendingBuckets.has(bucket)) {
      const bucketUrl = config.personalHashUrl.replace('bf-personal-00.json', `bf-personal-${bucket}.json`);
      pendingBuckets.set(bucket, fetch(bucketUrl + (refresh ? `${bucketUrl.includes('?') ? '&' : '?'}signup=${Date.now()}` : ''), {credentials: 'omit', cache: refresh ? 'no-store' : 'default', signal: AbortSignal.timeout(8000)})
        .then((response) => { if (!response.ok) throw new Error('BF hash bucket unavailable'); return response.json(); })
        .then((data) => {
          if (!Array.isArray(data.hashes) || data.hashes.some((h) => !/^[a-f0-9]{64}$/.test(h))) return;
          data.hashes.forEach((h) => hashes.add(h));
          hashCache.clear();
        }).catch(() => {}));
    }
    return pendingBuckets.get(bucket);
  }

  function activeCode() {
    if (!config.active) return '';
    const localCode = readLocalCode();
    if (isCode(localCode)) return localCode;
    // Without local storage (private mode) the cookie alone decides.
    const cookieCode = readCookieCode();
    if (!localCode && isCode(cookieCode)) return cookieCode;
    return '';
  }

  /* The `discount_code` cookie is always the latest /discount/<CODE> visit: Shopify sets it, and
     the Klaviyo welcome hand-off goes through the same endpoint. So a BF cookie that differs from
     the stored code is the newer intent (typically a visitor who already had a welcome code), and
     it replaces the stored code. */
  function adoptCookieCode() {
    if (!config.active) return;
    const cookieCode = readCookieCode();
    if (!isCode(cookieCode) || readLocalCode() === cookieCode) return;
    try {
      localStorage.setItem(CART_DISCOUNT_CODE_KEY, cookieCode);
      WELCOME_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      return;
    }
    window.dispatchEvent(new CustomEvent(CODE_CHANGED_EVENT));
  }

  // A confirmed signup activates its newly provisioned, hash-verified personal code before
  // success is shown. The test-link permission never calls this path.
  async function activateCode(code) {
    if (!config.active || !/^w[0-3][0-9a-f]{3}[a-z2-7]{10}$/.test(normalize(code))) throw Error('BF personal code required');
    await loadPersonalCode(code, true);
    if (!isCode(code)) throw Error('BF code not verified');
    localStorage.setItem(CART_DISCOUNT_CODE_KEY, normalize(code));
    WELCOME_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
    window.dispatchEvent(new CustomEvent(CODE_CHANGED_EVENT));
    // Shopify's cookie is a background handoff; the existing cart controller also reads the
    // stored code when the customer adds an item. No navigation or cart write here.
    const base = window.Shopify?.routes?.root || '/';
    fetch(`${base}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(location.pathname)}`, {credentials: 'same-origin', signal: AbortSignal.timeout(8000)}).catch(() => {});
  }

  function syncRootClass() {
    document.documentElement.classList.toggle(ACTIVE_CLASS, Boolean(activeCode()));
  }

  /* Same rule as the discount function: rounded down, to whole kroner in DKK and to the nearest x,95
     in every other currency. */
  function variantRate(value, fallback, max) {
    if (value === null || value === undefined || value === '') return fallback;
    const pct = Number(value);
    return Number.isInteger(pct) && pct >= 0 && pct <= max ? pct : fallback;
  }

  function unitPrice(price, pct, currency, fixedPrice) {
    if (!(pct > 0)) return price;
    const target = Number(fixedPrice);
    if (Number.isInteger(target) && target > 0 && target < price) return target;
    const raw = Math.round((price * (100 - pct)) / 100);
    const rounded = currency === 'DKK' ? Math.floor(raw / 100) * 100 : Math.floor((raw - 95) / 100) * 100 + 95;
    return rounded > 0 ? rounded : raw;
  }

  function formatMoney(cents, locale, currency) {
    const code = currency || 'DKK';
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'da-DK', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'symbol',
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${code}`;
    }
  }

  /* The amount in the Klarna line on the PDP (snippets/product-info.liquid, DK only): a quarter of the
     price, rounded down like Liquid's `divided_by`, in the line's own money format. Only the digits
     change, so the text around them stays as the server wrote it. */
  const KLARNA_AMOUNT = /\d[\d.]*,\d{2}/;
  function klarnaText(text, price) {
    const quarter = Math.floor(price / 4);
    const whole = String(Math.floor(quarter / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return text.replace(KLARNA_AMOUNT, `${whole},${String(quarter % 100).padStart(2, '0')}`);
  }
  const klarnaOriginals = new WeakMap();

  /* The gift code loads only for a visitor with BF access or a gift line from earlier, so nobody
     else downloads it or makes its requests. */
  let giftLoaded = false;
  function loadGift() {
    const gift = config.gift;
    if (giftLoaded || !gift?.script) return;
    let marked = false;
    try {
      marked = Boolean(localStorage.getItem('bf-gift:line'));
    } catch {
      /* no storage */
    }
    if (!activeCode() && !marked) return;
    giftLoaded = true;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = gift.css;
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = gift.script;
    document.head.appendChild(script);
  }

  window.bfInsider = { activateCode, isCode, activeCode, isActive: () => Boolean(activeCode()), unitPrice, formatMoney, variantRate };

  adoptCookieCode();
  syncRootClass();
  loadGift();
  window.bfInsider.ready = Promise.all([readCookieCode(), readLocalCode(), window.__bfTestCode].map((code) => loadPersonalCode(code))).then(() => {
    adoptCookieCode();
    // Test permission opens signup only. A confirmed signup or a personal mail link activates prices.
    syncRootClass();
    window.dispatchEvent(new CustomEvent(CODE_CHANGED_EVENT));
  });
  window.addEventListener(CODE_CHANGED_EVENT, syncRootClass);
  window.addEventListener(CODE_CHANGED_EVENT, loadGift);
  window.addEventListener('storage', (event) => {
    if (event.key === CART_DISCOUNT_CODE_KEY) syncRootClass();
  });

  if (customElements.get('bf-insider-price')) return;

  class BfInsiderPrice extends HTMLElement {
    connectedCallback() {
      this.variants = this.readVariants();
      this.amountElement = this.querySelector('[data-bf-amount]');
      this.anchorElement = this.querySelector('[data-bf-anchor]');
      this.saveElement = this.querySelector('[data-bf-save]');
      this.shippingFreeElement = this.querySelector('[data-bf-shipping-free]');
      this.shippingNoteElement = this.querySelector('[data-bf-shipping-note]');
      this.currentVariantId = this.dataset.selectedVariantId;

      this._onVariantChange = (event) => {
        if (this.dataset.fixedVariant === 'true') return;
        // variant:change bubbles to document from every product form on the page; only a variant
        // of this product moves this price (a PDP change must not blank the recommendation cards).
        const id = event.detail?.variant?.id;
        if (!id || !this.variants[String(id)]) return;
        this.currentVariantId = String(id);
        this.render();
      };
      this._onCodeChanged = () => this.render();
      this._onStorage = (event) => {
        if (event.key === CART_DISCOUNT_CODE_KEY) this.render();
      };

      document.addEventListener('variant:change', this._onVariantChange);
      window.addEventListener(CODE_CHANGED_EVENT, this._onCodeChanged);
      window.addEventListener('storage', this._onStorage);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._onVariantChange);
      window.removeEventListener(CODE_CHANGED_EVENT, this._onCodeChanged);
      window.removeEventListener('storage', this._onStorage);
      this._klarnaObserver?.disconnect();
      this._klarnaObserver = null;
    }

    readVariants() {
      const script = this.querySelector('[data-bf-variants]');
      try {
        return JSON.parse(script?.textContent || '[]').reduce((acc, variant) => {
          acc[String(variant.id)] = {
            price: Number(variant.price) || 0,
            fixedPrice: Number(variant.fixed_price) || 0,
            pct: variantRate(variant.pct, Number(this.dataset.pct), Number(this.dataset.maxPct || 50)),
            lowest: Number(variant.lowest_30d) || 0,
          };
          return acc;
        }, {});
      } catch {
        return {};
      }
    }

    render() {
      const variant = this.variants[String(this.currentVariantId)];
      const pct = variant?.pct;
      if (!activeCode() || !variant?.price || !(pct > 0) || !this.amountElement) {
        this.restoreKlarna();
        this.hidden = true;
        return;
      }

      const price = variant.price;
      const bfPrice = unitPrice(price, pct, this.dataset.currency, variant.fixedPrice);
      // Reference = lowest price of the last 30 days; never above today's price.
      const reference = variant.lowest > 0 ? Math.min(variant.lowest, price) : price;
      const showReduction = reference > bfPrice;

      const amount = this.formatMoney(bfPrice);
      const fromTemplate = this.dataset.fromTemplate;
      this.amountElement.textContent = fromTemplate ? fromTemplate.replace('__PRICE__', amount) : amount;
      if (this.anchorElement) {
        this.anchorElement.textContent = showReduction ? this.formatMoney(reference) : '';
        this.anchorElement.hidden = !showReduction;
      }

      if (this.saveElement) {
        this.saveElement.textContent = (this.saveElement.dataset.template || '').replace('__PRICE__', this.formatMoney(reference - bfPrice));
        this.saveElement.hidden = !showReduction;
      }

      const freeFrom = Number(this.dataset.deFreeShippingFrom) || 0;
      const shippingFree = freeFrom > 0 && bfPrice >= freeFrom;
      if (this.shippingFreeElement) this.shippingFreeElement.hidden = !shippingFree;
      if (this.shippingNoteElement) this.shippingNoteElement.hidden = shippingFree;

      this.hidden = false;
      // The PDP block wrapper is shared with the welcome element, which hides it while it has no
      // price of its own. The BF price is the price, so it re-opens the wrapper.
      const wrapper = this.closest('.product-info__block-item[data-block-type="welcome-discount-price"]');
      if (wrapper) wrapper.hidden = false;

      this._klarnaPrice = bfPrice;
      this.watchKlarna();
      this.syncKlarna();
    }

    klarnaNode() {
      const line = this.closest('.product-info')?.querySelector('.pkm-v4-klarna-line');
      return line && [...line.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && KLARNA_AMOUNT.test(node.data));
    }

    syncKlarna() {
      // Cards and cart rows sit inside .product-info too; only the PDP's own price has an observer.
      const node = this._klarnaObserver && this.klarnaNode();
      if (!node) return;
      if (!klarnaOriginals.has(node)) klarnaOriginals.set(node, node.data);
      const text = klarnaText(node.data, this._klarnaPrice);
      if (text !== node.data) node.data = text;
    }

    /* A variant change replaces the price block holding the line with the server's normal price,
       right after variant:change and without touching this element. The observer exists only while
       this price shows, so a visitor without a code never gets one. */
    watchKlarna() {
      const root = this.closest('.product-info');
      if (this._klarnaObserver || this.dataset.mode !== 'full' || this.dataset.currency !== 'DKK' || !root) return;
      this._klarnaObserver = new MutationObserver(() => this.syncKlarna());
      this._klarnaObserver.observe(root, { childList: true, subtree: true });
    }

    restoreKlarna() {
      if (!this._klarnaObserver) return;
      this._klarnaObserver.disconnect();
      this._klarnaObserver = null;
      const node = this.klarnaNode();
      if (node && klarnaOriginals.has(node)) node.data = klarnaOriginals.get(node);
    }

    formatMoney(cents) {
      return formatMoney(cents, this.dataset.locale, this.dataset.currency);
    }
  }

  customElements.define('bf-insider-price', BfInsiderPrice);
})();
